/**
 * CommandPaletteContext (PR_RUN_035)
 * ===================================
 * Replaces the `window.__openCommandPalette` global with a React Context.
 * The provider owns the open/closed boolean; `CommandPalette` reads/writes
 * via `useCommandPalette()`; any UI that wants to launch the palette (e.g.
 * the topbar "Jump to..." button) calls `openPalette()` from the hook.
 *
 * Test-agent code-review note (closed PR_RUN_035): the prior global hack
 * worked but bypassed React's data flow, made testing harder, and risked
 * key collisions in any embed scenario.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";

const CommandPaletteContext = createContext(null);

export const CommandPaletteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const togglePalette = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo(
    () => ({ open, setOpen, openPalette, closePalette, togglePalette }),
    [open, openPalette, closePalette, togglePalette],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
};

export const useCommandPalette = () => {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error(
      "useCommandPalette() called outside of <CommandPaletteProvider>. " +
      "Wrap your tree in <CommandPaletteProvider> before mounting consumers.",
    );
  }
  return ctx;
};
